import { useEffect } from 'react';
import { useForm, useFieldArray, useWatch, Controller, type Control } from 'react-hook-form';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Skeleton,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useUpstreamConfig,
  useUpdateUpstream,
  useDeleteUpstream,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import {
  useUpstreamProviderLimits,
  type ProviderUpstreamLimit,
} from '@/api/manticore/cdn-providers';
import { cdnProviderLabel } from '../../cdn-provider-display';
import { VendorDrift, useDomainBindingProviders } from '../../_shared/VendorDrift';
import { configTone, configLabel } from '../../shared';
import {
  UPSTREAM_FORM_DEFAULTS,
  SELECTOR_OPTIONS,
  PROTOCOL_OPTIONS,
  S3_PROVIDER_OPTIONS,
  ADDRESSING_OPTIONS,
  buildFormFromServer,
  hasUpstreamConfig,
  buildUpstreamPayload,
  getS3ServiceDefaults,
  s3OriginUrlPreview,
  type UpstreamFormValues,
} from './form';

type Ctl = Control<UpstreamFormValues>;

// ── Small RHF field wrappers (keep the sections readable) ────────────────
function TextField({
  control,
  name,
  label,
  description,
  placeholder,
  type,
}: {
  control: Ctl;
  name: keyof UpstreamFormValues | `urls.${number}.value` | `originHeaders.${number}.${'name' | 'value'}`;
  label?: string;
  description?: string;
  placeholder?: string;
  type?: string;
}) {
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field }) => (
        <Field label={label} description={description}>
          {(props) => (
            <Input
              {...props}
              type={type}
              placeholder={placeholder}
              value={(field.value as string) ?? ''}
              onChange={field.onChange}
            />
          )}
        </Field>
      )}
    />
  );
}

// Radix Select can't use an empty-string item value; this sentinel stands in
// for `''` (a valid "none" choice) at the Radix layer only.
const SELECT_NONE = '__none__';
const toSelectValue = (v: string) => (v === '' ? SELECT_NONE : v);
const fromSelectValue = (v: string) => (v === SELECT_NONE ? '' : v);

function SelectField({
  control,
  name,
  label,
  description,
  options,
}: {
  control: Ctl;
  name: keyof UpstreamFormValues;
  label: string;
  description?: string;
  options: { value: string; label: string; disabled?: boolean }[];
}) {
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field }) => (
        <Field label={label} description={description}>
          {(props) => (
            // Radix forbids an empty-string SelectItem value, but `''` is a real
            // value here (e.g. originCertId = no mTLS). Map `'' <-> SELECT_NONE`
            // so the form keeps `''` while Radix gets a non-empty item value.
            <Select value={toSelectValue(field.value as string)} onValueChange={(v) => field.onChange(fromSelectValue(v))}>
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.value} value={toSelectValue(o.value)} disabled={o.disabled}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      )}
    />
  );
}

function SwitchRow({
  control,
  name,
  title,
  description,
  invert,
}: {
  control: Ctl;
  name: keyof UpstreamFormValues;
  title: string;
  description?: string;
  invert?: boolean; // for "skip verify" which stores originTlsVerify=false
}) {
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field }) => (
        <SettingRow
          title={title}
          description={description}
          control={
            <Switch
              checked={invert ? !field.value : !!field.value}
              onCheckedChange={(v) => field.onChange(invert ? !v : v)}
            />
          }
        />
      )}
    />
  );
}

const grid2: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
};

// ── S3 fields (shared by setup + configured) ─────────────────────────────
function S3Fields({ control, setValue }: { control: Ctl; setValue: (n: keyof UpstreamFormValues, v: string) => void }) {
  const v = useWatch({ control });
  const onProvider = (service: string) => {
    const d = getS3ServiceDefaults(service, {
      s3AddressingStyle: (v.s3AddressingStyle as string) ?? 'virtual_hosted',
      s3Region: (v.s3Region as string) ?? 'us-east-1',
      s3Endpoint: (v.s3Endpoint as string) ?? '',
    });
    setValue('s3Service', service);
    setValue('s3AddressingStyle', d.s3AddressingStyle);
    setValue('s3Region', d.s3Region);
    setValue('s3Endpoint', d.s3Endpoint);
  };
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Controller
        control={control}
        name="s3Service"
        render={({ field }) => (
          <Field label="Provider" description="Auto-fills endpoint and region defaults for the selected provider.">
            {(props) => (
              <Select value={field.value} onValueChange={onProvider}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {S3_PROVIDER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        )}
      />
      <div style={grid2}>
        <TextField control={control} name="s3Bucket" label="Bucket name" placeholder="my-cdn-assets" />
        <TextField control={control} name="s3Region" label="Region" placeholder="us-east-1" />
        <TextField control={control} name="s3AccessKeyId" label="Access key ID" placeholder="AKIA…" />
        <TextField
          control={control}
          name="s3SecretAccessKey"
          label="Secret access key"
          type="password"
          placeholder="********"
          description="Required on every save — stored secrets are never displayed."
        />
      </div>
      <SelectField control={control} name="s3AddressingStyle" label="Addressing style" options={ADDRESSING_OPTIONS} />
      <TextField
        control={control}
        name="s3Endpoint"
        label="Custom endpoint (optional)"
        description="Override the default endpoint for custom S3-compatible providers."
        placeholder="s3.us-east-1.amazonaws.com"
      />
      <div
        style={{
          background: 'var(--vn-bg-subtle)',
          border: '1px solid var(--vn-border-default)',
          borderRadius: 'var(--vn-radius-md)',
          padding: 'var(--vn-space-3)',
          fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)',
          fontSize: 'var(--vn-font-size-sm)',
          color: 'var(--vn-fg-muted)',
        }}
      >
        <b style={{ color: 'var(--vn-fg-default)' }}>Origin URL: </b>
        {s3OriginUrlPreview({
          s3Bucket: (v.s3Bucket as string) ?? '',
          s3Region: (v.s3Region as string) ?? '',
          s3Endpoint: (v.s3Endpoint as string) ?? '',
          s3AddressingStyle: (v.s3AddressingStyle as string) ?? 'virtual_hosted',
        })}
      </div>
    </div>
  );
}

// ── HTTP origin servers (field array) ────────────────────────────────────
function OriginServers({ control }: { control: Ctl }) {
  const { fields, append, remove } = useFieldArray({ control, name: 'urls' });
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {fields.map((f, idx) => (
        <div key={f.id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          <Controller
            control={control}
            name={`urls.${idx}.value`}
            render={({ field }) => (
              <Input {...field} placeholder="http://10.0.0.1:8080" style={{ flex: 1 }} />
            )}
          />
          {fields.length > 1 && (
            <IconButton variant="ghost" size="sm" aria-label="Remove origin" onClick={() => remove(idx)}>
              <Trash2 size={15} />
            </IconButton>
          )}
        </div>
      ))}
      <div>
        <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => append({ value: '' })}>
          Add origin
        </Button>
      </div>
    </div>
  );
}

function OriginHeaders({ control }: { control: Ctl }) {
  const { fields, append, remove } = useFieldArray({ control, name: 'originHeaders' });
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {fields.map((f, idx) => (
        <div key={f.id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          <Controller
            control={control}
            name={`originHeaders.${idx}.name`}
            render={({ field }) => <Input {...field} placeholder="Header name" style={{ flex: 1 }} />}
          />
          <Controller
            control={control}
            name={`originHeaders.${idx}.value`}
            render={({ field }) => <Input {...field} placeholder="Header value" style={{ flex: 2 }} />}
          />
          <IconButton variant="ghost" size="sm" aria-label="Remove header" onClick={() => remove(idx)}>
            <Trash2 size={15} />
          </IconButton>
        </div>
      ))}
      <div>
        <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => append({ name: '', value: '' })}>
          Add header
        </Button>
      </div>
    </div>
  );
}

// ── Common sections (HTTP + S3) ──────────────────────────────────────────
function CommonSections({ control }: { control: Ctl }) {
  const cbEnabled = useWatch({ control, name: 'circuitBreakerEnabled' });
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Circuit breaker</CardTitle>
          <CardDescription>Trip the circuit when an origin keeps failing, protecting it from cascading load.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <SwitchRow control={control} name="circuitBreakerEnabled" title="Enable circuit breaker" />
          {cbEnabled && (
            <div style={grid2}>
              <TextField control={control} name="circuitBreakerThreshold" label="Failure threshold" type="number" description="Consecutive failures before tripping." />
              <TextField control={control} name="circuitBreakerRecoverySecs" label="Recovery interval (s)" type="number" description="Wait in Open state before testing origin." />
              <TextField control={control} name="circuitBreakerHalfOpen" label="Half-open probes" type="number" description="Successful probes required to fully close." />
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Origin shield</CardTitle>
          <CardDescription>An intermediate shielding server that protects the origin from direct peak traffic.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <TextField control={control} name="originShield" label="Origin shield address" placeholder="shield.example.com:443" />
          <SwitchRow control={control} name="originShieldTls" title="Use TLS for shield connections" />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Retry policy</CardTitle>
          <CardDescription>Which error conditions trigger an automatic retry to the next upstream origin.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <TextField
            control={control}
            name="retryOnStatus"
            label="Retry on status codes"
            placeholder="502, 503, 504"
            description="Comma-separated HTTP codes. Empty = retry on any connection error."
          />
          <SwitchRow control={control} name="retryOnConnectFailure" title="Retry on connect failure" />
          <SwitchRow control={control} name="retryOnTimeout" title="Retry on timeout" />
          <SwitchRow control={control} name="retryOnReset" title="Retry on connection reset" />
        </CardBody>
      </Card>
    </>
  );
}

// ── Secondary-provider limits alert ─────────────────────────────────────
// Renders a single informational Alert listing every enabled secondary provider
// that has known upstream field limitations. Uses provider display labels (never
// raw vendor brand names). Omitted when no limits are present.
function SecondaryLimitsAlert({ limits }: { limits: ProviderUpstreamLimit[] }) {
  if (limits.length === 0) return null;
  const lines = limits.map((l) => {
    const label = cdnProviderLabel(l.providerName);
    return `${label}: ${l.unsupportedFields.join(', ')}`;
  });
  return (
    <Alert tone="info" title="Some settings don't apply to all providers">
      The following fields are not mirrored to secondary providers when saving:
      <ul style={{ margin: 'var(--vn-space-1) 0 0', paddingLeft: 'var(--vn-space-4)' }}>
        {lines.map((line) => (
          <li key={line} style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {line}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

// ── Tab ──────────────────────────────────────────────────────────────────
export function OriginTab({
  domain,
  domainId,
  serviceId,
}: {
  domain: string;
  domainId: string;
  /** Umbrella service ID. When provided, surfaces a warning for secondary providers
   *  that cannot mirror all upstream settings. Optional — omitted when not available
   *  in the current route context. */
  serviceId?: string;
}) {
  const { show } = useToast();
  const { data: upstreamRead, isLoading } = useUpstreamConfig(domainId);
  const bindingProviders = useDomainBindingProviders(domainId);
  // Derive secondary-provider field limitations from the providers list.
  // No-op when serviceId is absent (hook returns [] and the alert is hidden).
  const secondaryLimits = useUpstreamProviderLimits(serviceId);
  const cfg = upstreamRead?.config ?? null;
  // The form edits the CORE (vEdge) config — the single source of truth.
  // Surface read problems explicitly instead of guessing from an empty map:
  // a failed core read keeps the form usable but warns; a domain with no core
  // binding at all is a legacy anomaly worth flagging.
  const coreProvider = upstreamRead?.coreProvider ?? '';
  const coreState = coreProvider ? upstreamRead?.vendors?.[coreProvider] : undefined;
  const coreReadFailed = !isLoading && coreState?.status === 'error';
  const noCoreBinding =
    !isLoading && !coreProvider && Object.keys(upstreamRead?.vendors ?? {}).length > 0;
  const isConfigured = hasUpstreamConfig(cfg ?? null);

  const { control, handleSubmit, reset, watch, setValue, formState } = useForm<UpstreamFormValues>({
    defaultValues: UPSTREAM_FORM_DEFAULTS,
  });
  const mode = watch('mode');
  const tlsToOrigin = watch('tlsToOrigin');

  useEffect(() => {
    if (isLoading) return;
    reset(buildFormFromServer(cfg ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, cfg]);

  const update = useUpdateUpstream(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'Origin configuration saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Could not save origin configuration', description: errorMessage(e, 'Could not save origin configuration') }),
  });
  const remove = useDeleteUpstream(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'origin', 'delete');
      show({ tone: 'success', title: 'Origin configuration removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove origin configuration', description: errorMessage(e, 'Could not remove origin configuration') }),
  });
  const busy = update.isPending || remove.isPending;

  const onSubmit = (values: UpstreamFormValues) => {
    const { payload, error } = buildUpstreamPayload(values);
    if (error || !payload) {
      show({ tone: 'danger', title: error ?? 'Invalid configuration' });
      return;
    }
    update.mutate(payload, { onSuccess: () => { trackFeatureAction('cdn', 'origin', isConfigured ? 'edit' : 'create'); reset(values); } });
  };

  const setVal = (n: keyof UpstreamFormValues, val: string) => setValue(n, val as never, { shouldDirty: true });

  if (isLoading) {
    return (
      <Section title="Origin">
        <Skeleton style={{ height: 280 }} />
      </Section>
    );
  }

  return (
    <Section
      title="Origin"
      description={`Backend origin configuration for ${domain}.`}
      actions={<StatusPill tone={configTone(isConfigured)}>{configLabel(isConfigured)}</StatusPill>}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {coreReadFailed && (
          <Alert tone="warning" title="Core configuration could not be read">
            The core provider didn't return the origin configuration
            {coreState?.error ? `: ${coreState.error}` : '.'} The values below may be stale or
            empty — saving will still write to the core configuration.
          </Alert>
        )}
        {noCoreBinding && (
          <Alert tone="info" title="This domain has no core binding">
            This domain was created outside the orchestrator's core plane and only exists on a
            secondary provider. Origin shown here may be incomplete; consider recreating the
            domain under its service to restore full management.
          </Alert>
        )}
        <SecondaryLimitsAlert limits={secondaryLimits} />
        <VendorDrift
          vendors={upstreamRead?.vendors}
          coreProvider={upstreamRead?.coreProvider}
          coreConfigured={!!upstreamRead?.config}
          bindingProviders={bindingProviders}
        />
        <Card>
          <CardHeader
            actions={
              <SegmentedControl
                items={[
                  { value: 'http', label: 'HTTP Origin' },
                  { value: 's3', label: 'S3 / Object Storage' },
                ]}
                value={mode}
                onValueChange={(v) => setValue('mode', v as 'http' | 's3', { shouldDirty: true })}
                aria-label="Origin mode"
              />
            }
          >
            <CardTitle>{mode === 'http' ? 'Origin servers' : 'S3-compatible origin'}</CardTitle>
            <CardDescription>The backend the CDN pulls content from.</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {mode === 'http' ? <OriginServers control={control} /> : <S3Fields control={control} setValue={setVal} />}
          </CardBody>
        </Card>

        {mode === 'http' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Connection settings</CardTitle>
              </CardHeader>
              <CardBody>
                <div style={grid2}>
                  <SelectField control={control} name="selector" label="Load balancing algorithm" options={SELECTOR_OPTIONS} />
                  <SelectField control={control} name="protocol" label="Protocol" options={PROTOCOL_OPTIONS} />
                  <TextField control={control} name="maxRetries" label="Max retries" type="number" description="0 = no retries." />
                  <TextField control={control} name="connectionPoolSize" label="Connection pool size" type="number" />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Origin headers</CardTitle>
              </CardHeader>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <div style={grid2}>
                  <TextField control={control} name="hostHeader" label="Host header override" placeholder="origin.example.com" description="Empty = use the CDN domain name." />
                  <TextField control={control} name="sniOverride" label="SNI override" description="Empty = auto-detect from origin URL." />
                </div>
                <div>
                  <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>Custom headers to origin</div>
                  <OriginHeaders control={control} />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>TLS to origin</CardTitle>
                <CardDescription>Encrypt connections from edge nodes to your origin servers.</CardDescription>
              </CardHeader>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <SwitchRow control={control} name="tlsToOrigin" title="Use TLS for origin connections" />
                {tlsToOrigin && (
                  <>
                    <SelectField
                      control={control}
                      name="originCertId"
                      label="mTLS client certificate"
                      description="Upload one in the SSL tab first. Select a certificate for mutual TLS to origin."
                      options={[{ value: '', label: 'None (no mTLS)' }]}
                    />
                    <Alert tone="warning" icon={<AlertTriangle size={16} />}>
                      <SwitchRow
                        control={control}
                        name="originTlsVerify"
                        invert
                        title="Skip certificate verification"
                        description="Enable for origins with self-signed certs. Disables TLS certificate + hostname verification."
                      />
                    </Alert>
                  </>
                )}
              </CardBody>
            </Card>
          </>
        )}

        {mode === 's3' && (
          <Card>
            <CardHeader>
              <CardTitle>Origin headers</CardTitle>
            </CardHeader>
            <CardBody>
              <OriginHeaders control={control} />
            </CardBody>
          </Card>
        )}

        <CommonSections control={control} />

        {!isConfigured && (
          <Alert tone="info">
            Advanced features like health checks and circuit breakers become available after saving the initial setup.
          </Alert>
        )}

        <ActionBar
          start={
            isConfigured ? (
              <Button variant="secondary" disabled={busy} onClick={() => remove.mutate()}>
                Remove
              </Button>
            ) : undefined
          }
          end={
            <>
              {isConfigured && (
                <Button
                  variant="secondary"
                  disabled={!formState.isDirty || busy}
                  onClick={() => reset(buildFormFromServer(cfg ?? null))}
                >
                  Cancel
                </Button>
              )}
              <Button disabled={busy || (isConfigured && !formState.isDirty)} onClick={handleSubmit(onSubmit)}>
                {update.isPending ? 'Saving…' : isConfigured ? 'Save' : 'Create'}
              </Button>
            </>
          }
        />
      </div>
    </Section>
  );
}
